import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

const accentMap: Record<string, string> = {
  primary: 'bg-primary-100 text-primary-800 border-primary-200',
  accent: 'bg-accent-100 text-accent-800 border-accent-200',
  secondary: 'bg-secondary-100 text-secondary-900 border-secondary-200',
};

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'feature' | 'wide' | 'compact';
  showCover?: boolean;
}

export default function ArticleCard({ article, variant = 'default', showCover = true }: ArticleCardProps) {
  const category = categories.find((c) => c.slug === article.categorySlug);
  const badge = category ? accentMap[category.accent] : accentMap.primary;

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex items-start gap-3 py-3 border-b border-background-200 last:border-b-0 cursor-pointer"
      >
        <span className="mt-0.5 w-6 h-6 shrink-0 flex items-center justify-center rounded-md bg-background-100 text-foreground-600 text-xs">
          <i className="ri-fire-line"></i>
        </span>
        <span className="flex-1">
          <span className="block text-sm text-foreground-900 group-hover:text-primary-600 transition-colors leading-snug">
            {article.title}
          </span>
          <span className="mt-1 block text-xs text-foreground-500">{article.publishedAt}</span>
        </span>
      </Link>
    );
  }

  if (variant === 'feature') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group block h-full rounded-lg border border-background-200 bg-background-50 overflow-hidden hover:border-primary-300 transition-colors cursor-pointer"
      >
        {showCover && (
          <div className="w-full h-52 md:h-64 overflow-hidden bg-background-100">
            <img
              src={article.cover}
              alt={`${article.title} · 17c吃瓜热门事件配图`}
              title={`${article.title} · 今日热门事件`}
              className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-500"
            />
          </div>
        )}
        <div className="p-4 md:p-5">
          <div className="flex items-center gap-2 text-xs mb-3 flex-wrap">
            <span className={`px-2 py-0.5 rounded-full border ${badge}`}>{category?.name}</span>
            <span className="text-foreground-500">{article.publishedAt}</span>
          </div>
          <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors">
            {article.title}
          </h3>
          <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">{article.summary}</p>
        </div>
      </Link>
    );
  }

  if (variant === 'wide') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-4 rounded-lg border border-background-200 bg-background-50 p-3 hover:border-primary-300 transition-colors cursor-pointer"
      >
        {showCover && (
          <div className="w-full h-40 sm:h-32 rounded-md overflow-hidden bg-background-100">
            <img
              src={article.cover}
              alt={`${article.title} · 17c吃瓜事件配图`}
              title={`${article.title} · 最新爆料`}
              className="w-full h-full object-cover object-top group-hover:scale-[1.04] transition-transform duration-500"
            />
          </div>
        )}
        <div className="flex flex-col justify-center">
          <div className="flex items-center gap-2 text-xs mb-2 flex-wrap">
            <span className={`px-2 py-0.5 rounded-full border ${badge}`}>{category?.name}</span>
            <span className="text-foreground-500">{article.readTime}</span>
          </div>
          <h3 className="font-heading text-base font-bold text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors">
            {article.title}
          </h3>
          <p className="mt-1.5 text-sm text-foreground-600 line-clamp-2">{article.summary}</p>
          <div className="mt-2 flex items-center gap-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-fire-line text-primary-500"></i>
              {article.heat}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.views}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group flex flex-col h-full rounded-lg border border-background-200 bg-background-50 overflow-hidden hover:border-primary-300 transition-colors cursor-pointer"
    >
      {showCover && (
        <div className="w-full h-44 overflow-hidden bg-background-100">
          <img
            src={article.cover}
            alt={`${article.title} · 热门事件配图`}
            title={`${article.title} · 全网热议`}
            className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-500"
          />
        </div>
      )}
      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-center gap-2 text-xs mb-2.5 flex-wrap">
          <span className={`px-2 py-0.5 rounded-full border ${badge}`}>{category?.name}</span>
          <span className="text-foreground-500">{article.readTime}</span>
        </div>
        <h3 className="font-heading text-base font-bold text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors">
          {article.title}
        </h3>
        <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-3 flex-1">
          {article.summary}
        </p>
        <div className="mt-3 pt-3 border-t border-background-200 flex items-center justify-between text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-fire-line text-primary-500"></i>
            热度 {article.heat}
          </span>
          <span>{article.publishedAt}</span>
        </div>
      </div>
    </Link>
  );
}